import React, { useEffect } from "react";
import { DropUp, Invoice, Sales, DropDwn, WarningIcon } from "../../assets/svg";
import RippleLoader from "../../components/loader/RippleLoader";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardData } from "../../redux/dashboard/slice";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selector
  const { getDashboardDataList, loading } = useSelector((store) => ({
    getDashboardDataList:
      store?.dashboardData?.getDashboardDataStatus?.data?.data,
    loading: store?.dashboardData?.loading,
  }));

  useEffect(() => {
    dispatch(getDashboardData({}));
  }, []);

  const StatusCard = ({ title, count, percentage, danger }) => (
    <div
      className={`p-4 rounded-lg shadow-md border border-borderLine ${
        danger ? "bg-red-100" : "bg-white"
      }`}
    >
      <h3 className="font-Montserrat text-xl font-medium text-primaryBlack">
        {title}
      </h3>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-normal font-Proxima text-primaryBlack">
          <p className="text-xs font-normal font-Proxima text-textSecondary">
            {title === "Active Outlets"
              ? "Total"
              : title === "On Recurring"
              ? "Total Left/Total Count"
              : "Total Amount"}
          </p>
          {count?.toLocaleString("en-IN")}
        </span>
        <span
          className={`text-xs text-white py-1 px-2 rounded-3xl flex items-center ${
            percentage?.includes("-") ? "bg-red" : "bg-bgGreen"
          }`}
        >
          {percentage?.includes("-") ? <DropDwn /> : <DropUp />}
          {percentage?.startsWith("-")
            ? percentage.replace("-", "")
            : percentage}
        </span>
      </div>
    </div>
  );

  const InvoiceCard = ({ label, value, danger, count, className }) => (
    <div className="flex flex-col">
      <span className="font-normal font-Proxima text-sm text-primaryBlack">
        {label}
      </span>
      <span className="font-Proxima text-sm text-textSecondary">
        Total Count: {count?.toLocaleString("en-IN")}
      </span>
      <span className={`text-3xl font-normal font-Montserrat ${className}`}>
        $
        {parseFloat(value).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="mb-6 md:mb-8">
        <h1 className="text-lg md:text-xl font-medium font-Montserrat">
          Welcome Back! 👋
        </h1>
      </div>

      {loading ? (
        <RippleLoader />
      ) : (
        <>
          {/* Status Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <StatusCard
              title="Active Outlets"
              count={getDashboardDataList?.activeOutlets}
              percentage={getDashboardDataList?.percentageOutletIncrease}
            />
            <StatusCard
              title="Active Outlet"
              count={getDashboardDataList?.activeOutletsTotal}
              percentage={getDashboardDataList?.percentageOutletIncrease}
            />
            <StatusCard
              title="On Recurring"
              count={getDashboardDataList?.company_contract}
              percentage={getDashboardDataList?.percentageOnRecurringIncrease}
            />
          </div>

          {/* Invoices & Salesperson Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Salesperson Ranking */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 border border-borderLine">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="flex items-center gap-2">
                  <WarningIcon className="w-6 h-6 md:w-8 md:h-8" />
                  <h2 className="text-xl md:text-2xl font-normal font-Montserrat text-primaryBlack">
                    Service Report Yet to Sign Off
                  </h2>
                </div>
                <p
                  className="text-themeBtn border-b border-b-lightOrangeTheme cursor-pointer text-sm md:text-base font-normal"
                  onClick={() => navigate("/service-report")}
                >
                  View All
                </p>
              </div>
              <p className="font-Proxima text-sm text-textSecondary mt-1 mb-3">
                Sign off as soon as possible to prevent stoppage of scheduling
                services
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed rounded-t-lg">
                  <thead className="text-xs md:text-sm bg-bgLightTheme rounded-t-lg border-b border-b-lightPrimaryGrey text-start">
                    <tr className=" bg-lightTheme">
                      <th className="p-2  py-4 px-4 text-left font-light w-20 text-primaryBlack">
                        No.
                      </th>
                      <th className="p-2  py-4 px-4 text-left font-light text-primaryBlack">
                        Company/Outlet
                      </th>
                      <th className="p-2  py-4 px-4 text-left font-light text-primaryBlack">
                        Service Report Link
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getDashboardDataList?.service_reports?.map(
                      (item, index) => (
                        <tr key={index} className="text-primaryBlack">
                          <td className="px-2 md:px-5 py-1">{index + 1}</td>
                          <td className="px-2 md:px-5 py-1">
                            {item?.company_outlet}
                          </td>
                          <td className="px-2 md:px-5 py-1">
                            <Link className="text-themeBtn">
                              {item?.service_report_id}
                            </Link>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Invoices Card */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 border border-borderLine sm:h-52">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="flex items-center gap-2">
                  <Invoice className="w-6 h-6 md:w-8 md:h-8" />
                  <h2 className="text-xl md:text-2xl font-normal text-primaryBlack font-Montserrat">
                    Billing
                  </h2>
                </div>
                <p
                  className="text-themeBtn border-b border-b-lightOrangeTheme cursor-pointer text-sm md:text-base font-normal"
                  onClick={() =>
                    navigate("/sales", { state: { tab: "Invoice" } })
                  }
                >
                  See All
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mt-4 md:px-8">
                <InvoiceCard
                  label="Awaiting Payment"
                  className="text-black"
                  value={
                    getDashboardDataList?.invoices.awaitingPayment.totalAmount
                  }
                  count={
                    getDashboardDataList?.invoices.awaitingPayment.totalCount
                  }
                />
                <InvoiceCard
                  label="Overdue"
                  className="text-red"
                  value={getDashboardDataList?.invoices?.OverDue?.totalAmount}
                  count={getDashboardDataList?.invoices?.OverDue?.totalCount}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
